import { useState, useEffect } from "react";
import { fetchWeeklyRolls, parseWeekKey } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Weekly Mod Rolls filter panel on
 * ProductionMilestonesPage — the Mon–Fri rolls/wraps table and bar chart for
 * one selected business week.
 *
 * ── Trigger contract ──────────────────────────────────────────────────────
 *
 * Re-fetches whenever `appliedWeekKey` changes. The page already owns the
 * full Apply-button flow (handleApplyRolls commits `rollWeekKey` →
 * `appliedRollWeekKey` only when the user clicks Apply, not on every
 * dropdown change) — this hook just reacts to that committed value, so it
 * doesn't need to know about the draft `rollYear` / `rollMonth` / `rollWeekKey`
 * state at all.
 *
 * @param {string} appliedWeekKey
 *   The committed business-week key in "YYYY-monthIdx-Wn" format (the page's
 *   `appliedRollWeekKey`). Pass "" / null / undefined for "no week selected
 *   yet" — the hook returns its idle defaults and does not fetch.
 *
 * ── Auto-filter from the ModRollTime modal ─────────────────────────────────
 *
 * When arriving via navigate("/production-milestones", { state: { rollDate } })
 * from the ModRollTime modal, ProductionMilestonesPage resolves rollDate →
 * weekKey SYNCHRONOUSLY during its own render (see `_initRoll` on the page),
 * before this hook ever mounts — so `appliedRollWeekKey`'s INITIAL useState
 * value is already the deep-linked week, not "". That means this hook needs
 * no special-case branch for "came from a modal click" vs. "user picked a
 * week and clicked Apply": both arrive here the exact same way — as a
 * non-empty appliedWeekKey on first render or after a state update. The
 * hook is intentionally dumb about *why* the key changed.
 *
 * ── Caching ─────────────────────────────────────────────────────────────────
 *
 * No module-level cache here (unlike useHomepageData / useProductionMilestones).
 * Each distinct week is a small, cheap query and the user may page through
 * many different weeks in one session — caching every visited week would grow
 * unbounded for little benefit. A simple per-key in-memory map with a cap
 * would be the next step if this becomes a measured cost.
 *
 * @returns {{
 *   weekLabel:     string,
 *   weekStart:     string,
 *   weekEnd:       string,
 *   spansBoundary: boolean,
 *   rollLeader:    string | null,
 *   days: Record<"Mon"|"Tue"|"Wed"|"Thu"|"Fri", {
 *     projRolls: number, actRolls: number, projWraps: number, actWraps: number,
 *   }> | null,
 *   loading:    boolean,
 *   error:      string | null,
 *   noDataForWeek: boolean,  // true when a week WAS requested but the backend
 *                            // returned an all-zero/empty result — distinct
 *                            // from "no week selected" (appliedWeekKey falsy)
 * }}
 */

const IDLE_STATE = {
  weekLabel: "",
  weekStart: "",
  weekEnd: "",
  spansBoundary: false,
  rollLeader: null,
  days: null,
};

const useWeeklyRolls = (appliedWeekKey) => {
  const [data, setData] = useState(IDLE_STATE);
  // Initialise loading=true whenever a key is already present on mount (e.g.
  // _initRoll resolved a deep-linked or default week before this hook's
  // first effect runs). Starting at false here left a one-render gap where
  // appliedWeekKey was truthy, days was still null (IDLE_STATE), and
  // noDataForWeek was also false — falling through to the data-render branch
  // with no data and crashing on `.label`. Lazy-initialising from the prop
  // closes that gap.
  const [loading, setLoading] = useState(() => Boolean(appliedWeekKey));
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    // No week selected yet (initial UI state before any week is applied,
    // or after a year/month dropdown change resets rollWeekKey to "").
    if (!appliedWeekKey) {
      setData(IDLE_STATE);
      setLoading(false);
      setError(null);
      return;
    }

    const parsed = parseWeekKey(appliedWeekKey);

    if (!parsed) {
      // Malformed key — surface as an error rather than silently no-op'ing,
      // since this indicates a bug in the page's key generation rather than
      // a normal "nothing selected" state.
      setError(`Invalid week key format: "${appliedWeekKey}"`);
      setData(IDLE_STATE);
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchWeeklyRolls(parsed, controller.signal);
        setData(result);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load weekly rolls data.");
        setData(IDLE_STATE);
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [appliedWeekKey]);

  // A week was requested (key present) but the table came back fully empty —
  // distinct from "no week selected" so the page can render its existing
  // "No data found for this timeframe" empty state rather than the initial
  // unfiltered placeholder.
  const noDataForWeek = Boolean(
    appliedWeekKey &&
    !loading &&
    !error &&
    data.days &&
    Object.values(data.days).every(
      (d) =>
        d.projRolls === 0 &&
        d.actRolls === 0 &&
        d.projWraps === 0 &&
        d.actWraps === 0,
    ),
  );

  return { ...data, loading, error, noDataForWeek };
};

export default useWeeklyRolls;
